import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, Newspaper, Mail, FileText } from 'lucide-react';
import { SUPPORT_EMAIL } from '../config/contact';
import {
  EDITORIAL_COVERAGE,
  FEATURED_OUTLETS,
  PRESS_RELEASE_DATE,
  PRESS_RELEASE_HEADLINE,
  PRESS_RELEASE_URL,
  PRESS_STATS,
} from '../data/press';

const EASE = [0.25, 0, 0, 1];

const FadeUp = ({ children, className = '', delay = 0 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.72, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

const ExternalLinkIcon = () => (
  <ArrowUpRight
    className="w-4 h-4 text-gray-600 group-hover:text-blue-400 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300 flex-shrink-0"
    aria-hidden="true"
  />
);

const PressPage = () => (
  <div className="pt-24 text-white">

    {/* ══════════════════════════════════════════════════
        HERO
    ══════════════════════════════════════════════════ */}
    <section className="py-20 px-6">
      <div className="max-w-4xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 mb-6">
            <Newspaper className="w-4 h-4 text-blue-400" aria-hidden="true" />
            <span className="text-blue-400 text-sm font-medium">Press &amp; media</span>
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
            ClearWay in the news.
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
            On {PRESS_RELEASE_DATE}, ClearWay announced its obstacle-detection add-on for the
            standard white cane. The story ran in India&apos;s largest newspapers, business dailies
            and wire agencies, and drew independent coverage from the technology press.
          </p>
        </motion.div>
      </div>
    </section>


    {/* ══════════════════════════════════════════════════
        STATS
    ══════════════════════════════════════════════════ */}
    <section className="py-12 px-6 bg-[#0f0f0f] border-y border-white/5">
      <div className="max-w-5xl mx-auto">
        <dl className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {PRESS_STATS.map((stat, i) => (
            <FadeUp key={stat.label} delay={0.06 * i}>
              <div className="p-6 rounded-2xl bg-blue-500/[0.04] border border-blue-500/15 text-center">
                <dd className="text-4xl md:text-5xl font-semibold text-white mb-2 tabular-nums">{stat.value}</dd>
                <dt className="text-xs text-gray-500 tracking-widest uppercase">{stat.label}</dt>
              </div>
            </FadeUp>
          ))}
        </dl>
        <FadeUp delay={0.2}>
          <p className="text-center text-xs text-gray-600 mt-6">
            Audience figure is the combined monthly traffic of the sites that carried the story, as reported
            by PR Newswire, not a count of readers.
          </p>
        </FadeUp>
      </div>
    </section>


    {/* ══════════════════════════════════════════════════
        INDEPENDENT EDITORIAL COVERAGE
    ══════════════════════════════════════════════════ */}
    <section className="py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <FadeUp className="mb-12">
          <p className="text-blue-400 text-sm font-medium tracking-widest uppercase mb-3">Editorial coverage</p>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4">Written about ClearWay.</h2>
          <p className="text-gray-400 max-w-2xl">
            These pieces were reported and written in each outlet&apos;s own words, rather than
            republished from our announcement.
          </p>
        </FadeUp>

        <div className="grid md:grid-cols-3 gap-6">
          {EDITORIAL_COVERAGE.map((piece, i) => (
            <FadeUp key={piece.href + piece.outlet} delay={0.08 * i}>
              <a
                href={piece.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col h-full p-7 rounded-3xl bg-gradient-to-b from-gray-800/50 to-transparent border border-gray-800/50 hover:border-blue-500/25 focus-visible:border-blue-500/40 outline-none transition-colors duration-300"
              >
                <div className="flex items-start justify-between gap-3 mb-5">
                  <div>
                    <p className="text-white font-semibold">{piece.outlet}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{piece.date}</p>
                  </div>
                  <ExternalLinkIcon />
                </div>
                <h3 className="text-lg font-semibold leading-snug mb-3 group-hover:text-blue-100 transition-colors">
                  {piece.headline}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed flex-1">{piece.summary}</p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm text-blue-400 group-hover:text-blue-300 transition-colors">
                  Read the article <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                </span>
              </a>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>


    {/* ══════════════════════════════════════════════════
        NATIONAL & WIRE COVERAGE
    ══════════════════════════════════════════════════ */}
    <section className="py-24 px-6 bg-[#0f0f0f]">
      <div className="max-w-6xl mx-auto">
        <FadeUp className="mb-12">
          <p className="text-blue-400 text-sm font-medium tracking-widest uppercase mb-3">National &amp; wire coverage</p>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4">Where the announcement ran.</h2>
          <p className="text-gray-400 max-w-2xl">
            Thirteen of India&apos;s best-known news brands carried the launch announcement in full
            on {PRESS_RELEASE_DATE}. Each name links to that outlet&apos;s copy.
          </p>
        </FadeUp>

        <ol
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.06] rounded-2xl overflow-hidden"
          aria-label="Outlets that carried the ClearWay announcement"
        >
          {FEATURED_OUTLETS.map((outlet, i) => (
            <motion.li
              key={outlet.name}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.5, delay: 0.03 * i, ease: EASE }}
              className="bg-[#0f0f0f]"
            >
              <a
                href={outlet.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center justify-between gap-4 p-5 h-full hover:bg-white/[0.03] focus-visible:bg-white/[0.03] outline-none transition-colors duration-300"
                aria-label={`${outlet.name} — read ClearWay coverage (opens in a new tab)`}
              >
                <span className="flex items-baseline gap-4 min-w-0">
                  <span className="text-xs text-gray-600 tabular-nums w-6 flex-shrink-0">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-white font-semibold tracking-tight truncate">{outlet.name}</span>
                    <span className="block text-[11px] uppercase tracking-widest text-gray-500 mt-1">{outlet.kind}</span>
                  </span>
                </span>
                <ExternalLinkIcon />
              </a>
            </motion.li>
          ))}
        </ol>

        <FadeUp delay={0.1}>
          <p className="text-xs text-gray-600 mt-6">
            A further 147 regional, technology and syndication sites republished the announcement.
          </p>
        </FadeUp>
      </div>
    </section>


    {/* ══════════════════════════════════════════════════
        THE ANNOUNCEMENT + MEDIA ENQUIRIES
    ══════════════════════════════════════════════════ */}
    <section className="py-24 px-6">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-6">
        <FadeUp>
          <a
            href={PRESS_RELEASE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex flex-col h-full p-8 rounded-3xl bg-gradient-to-br from-blue-500/[0.08] to-transparent border border-blue-500/20 hover:border-blue-500/35 focus-visible:border-blue-500/45 outline-none transition-colors duration-300"
          >
            <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 mb-6">
              <FileText className="w-5 h-5" aria-hidden="true" />
            </div>
            <p className="text-blue-400 text-xs font-semibold tracking-widest uppercase mb-2">The announcement</p>
            <h3 className="text-xl font-semibold leading-snug mb-3">{PRESS_RELEASE_HEADLINE}</h3>
            <p className="text-gray-400 text-sm leading-relaxed flex-1">
              Released via PR Newswire on {PRESS_RELEASE_DATE}. The original text, photo and
              company details are available on the wire.
            </p>
            <span className="mt-6 inline-flex items-center gap-1.5 text-sm text-blue-400 group-hover:text-blue-300 transition-colors">
              Read the release on PR Newswire <ArrowUpRight className="w-3.5 h-3.5" aria-hidden="true" />
            </span>
          </a>
        </FadeUp>

        <FadeUp delay={0.1}>
          <div className="flex flex-col h-full p-8 rounded-3xl bg-gradient-to-b from-gray-800/50 to-transparent border border-gray-800/50">
            <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-gray-300 mb-6">
              <Mail className="w-5 h-5" aria-hidden="true" />
            </div>
            <p className="text-gray-400 text-xs font-semibold tracking-widest uppercase mb-2">Media enquiries</p>
            <h3 className="text-xl font-semibold leading-snug mb-3">Writing about ClearWay?</h3>
            <p className="text-gray-400 text-sm leading-relaxed flex-1">
              We are happy to arrange interviews with the founder, share high-resolution product
              photography, and provide trial data from Kamla Mehta School for the Blind, Mumbai.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <a
                href={`mailto:${SUPPORT_EMAIL}?subject=Media%20enquiry%20about%20ClearWay`}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-white text-black text-sm font-medium hover:bg-gray-100 transition-colors"
              >
                Email the team
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </a>
              <Link
                to="/impact"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-white/10 text-white text-sm font-medium hover:border-white/20 hover:bg-white/5 transition-colors"
              >
                See the trial results
              </Link>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  </div>
);

export default PressPage;
